import { loadOfflineSnapshot, saveOfflineSnapshot } from '@/lib/offline/dataStore';
import type { InventoryItemRecord } from '@/types/inventory';

const NAMESPACE = 'inventory';

type InventoryCacheMutation =
  | {
      kind: 'adjust';
      itemId: string;
      expectedOnHand: number;
      delta: number;
      timestamp: number;
      lastConflictNotifiedAt?: number;
    }
  | {
      kind: 'reorder';
      itemId: string;
      expectedReorderPoint: number;
      timestamp: number;
      lastConflictNotifiedAt?: number;
    };

export type InventoryConflictKind = 'adjust' | 'reorder';

export interface InventoryConflictRecord {
  itemId: string;
  kind: InventoryConflictKind;
  expected: number;
  actual: number | null;
  delta?: number;
  timestamp: number;
}

export interface InventoryCacheState {
  items: InventoryItemRecord[];
  pendingMutations: InventoryCacheMutation[];
  lastSyncedAt: number | null;
  lastNetworkVersion: number | null;
}

const DEFAULT_STATE: InventoryCacheState = {
  items: [],
  pendingMutations: [],
  lastSyncedAt: null,
  lastNetworkVersion: null,
};

const isInventoryItemRecord = (value: unknown): value is InventoryItemRecord => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Partial<InventoryItemRecord>;
  return typeof record.id === 'string' && typeof record.name === 'string' && typeof record.onHand === 'number';
};

const isMutation = (value: unknown): value is InventoryCacheMutation => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as InventoryCacheMutation;
  if (record.kind === 'adjust') {
    return (
      typeof record.itemId === 'string' &&
      typeof record.expectedOnHand === 'number' &&
      typeof record.delta === 'number' &&
      typeof record.timestamp === 'number'
    );
  }
  if (record.kind === 'reorder') {
    return (
      typeof record.itemId === 'string' &&
      typeof record.expectedReorderPoint === 'number' &&
      typeof record.timestamp === 'number'
    );
  }
  return false;
};

const cloneItem = (item: InventoryItemRecord): InventoryItemRecord => ({
  ...item,
});

const normalizeState = (raw: unknown): InventoryCacheState => {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_STATE };
  }
  const data = raw as Partial<InventoryCacheState>;
  const items = Array.isArray(data.items) ? data.items.filter(isInventoryItemRecord).map(cloneItem) : [];
  const pendingMutations = Array.isArray(data.pendingMutations)
    ? data.pendingMutations.filter(isMutation).map((mutation) => ({ ...mutation }))
    : [];
  const lastSyncedAt = typeof data.lastSyncedAt === 'number' ? data.lastSyncedAt : null;
  const lastNetworkVersion = typeof data.lastNetworkVersion === 'number' ? data.lastNetworkVersion : null;
  return {
    items,
    pendingMutations,
    lastSyncedAt,
    lastNetworkVersion,
  };
};

const computeInventoryVersion = (items: InventoryItemRecord[]): number =>
  items.reduce((acc, item) => Math.max(acc, Number.isFinite(item.updatedAt) ? item.updatedAt : 0), 0);

const persistState = (tenantSlug: string, state: InventoryCacheState): void => {
  saveOfflineSnapshot(NAMESPACE, tenantSlug, state, state.lastNetworkVersion ?? undefined);
};

export const loadInventoryCache = (tenantSlug: string): InventoryCacheState | null => {
  const snapshot = loadOfflineSnapshot<InventoryCacheState>(NAMESPACE, tenantSlug);
  if (!snapshot) {
    return null;
  }
  return normalizeState(snapshot.data);
};

const mergeAdjustMutation = (
  list: InventoryCacheMutation[],
  next: Extract<InventoryCacheMutation, { kind: 'adjust' }>,
): InventoryCacheMutation[] => {
  const existingIndex = list.findIndex((mutation) => mutation.kind === 'adjust' && mutation.itemId === next.itemId);
  if (existingIndex === -1) {
    return [...list, next];
  }
  const existing = list[existingIndex] as Extract<InventoryCacheMutation, { kind: 'adjust' }>;
  const merged: Extract<InventoryCacheMutation, { kind: 'adjust' }> = {
    kind: 'adjust',
    itemId: next.itemId,
    expectedOnHand: next.expectedOnHand,
    delta: existing.delta + next.delta,
    timestamp: next.timestamp,
  };
  return [...list.slice(0, existingIndex), merged, ...list.slice(existingIndex + 1)];
};

const mergeReorderMutation = (
  list: InventoryCacheMutation[],
  next: Extract<InventoryCacheMutation, { kind: 'reorder' }>,
): InventoryCacheMutation[] => {
  const existingIndex = list.findIndex((mutation) => mutation.kind === 'reorder' && mutation.itemId === next.itemId);
  if (existingIndex === -1) {
    return [...list, next];
  }
  return [...list.slice(0, existingIndex), next, ...list.slice(existingIndex + 1)];
};

const upsertItem = (
  items: Map<string, InventoryItemRecord>,
  mutation: InventoryCacheMutation,
  fallbackItem?: InventoryItemRecord | null,
): InventoryItemRecord | null => {
  if (mutation.kind === 'adjust') {
    const target = items.get(mutation.itemId) ?? (fallbackItem ? cloneItem(fallbackItem) : null);
    if (!target) {
      return null;
    }
    const nextOnHand = mutation.expectedOnHand;
    const nextUpdatedAt = Math.max(target.updatedAt, mutation.timestamp);
    const updated: InventoryItemRecord = {
      ...target,
      onHand: nextOnHand,
      updatedAt: nextUpdatedAt,
    };
    items.set(updated.id, updated);
    return updated;
  }

  if (mutation.kind === 'reorder') {
    const target = items.get(mutation.itemId) ?? (fallbackItem ? cloneItem(fallbackItem) : null);
    if (!target) {
      return null;
    }
    const updated: InventoryItemRecord = {
      ...target,
      reorderPoint: mutation.expectedReorderPoint,
      updatedAt: Math.max(target.updatedAt, mutation.timestamp),
    };
    items.set(updated.id, updated);
    return updated;
  }

  return null;
};

export const recordInventoryAdjustment = (
  tenantSlug: string,
  itemId: string,
  delta: number,
): InventoryItemRecord | null => {
  const snapshot = loadInventoryCache(tenantSlug) ?? { ...DEFAULT_STATE };
  const itemsMap = new Map(snapshot.items.map((item) => [item.id, cloneItem(item)]));
  const target = itemsMap.get(itemId);
  if (!target) {
    return null;
  }
  const nextOnHand = target.onHand + delta;
  const timestamp = Date.now();
  const mutation: Extract<InventoryCacheMutation, { kind: 'adjust' }> = {
    kind: 'adjust',
    itemId,
    expectedOnHand: nextOnHand,
    delta,
    timestamp,
  };
  const pendingMutations = mergeAdjustMutation(snapshot.pendingMutations, mutation);
  const updated = upsertItem(itemsMap, mutation);

  const nextState: InventoryCacheState = {
    items: Array.from(itemsMap.values()),
    pendingMutations,
    lastSyncedAt: snapshot.lastSyncedAt,
    lastNetworkVersion: snapshot.lastNetworkVersion,
  };
  persistState(tenantSlug, nextState);

  return updated ?? null;
};

export const recordInventoryReorderPoint = (
  tenantSlug: string,
  itemId: string,
  reorderPoint: number,
): InventoryItemRecord | null => {
  const snapshot = loadInventoryCache(tenantSlug) ?? { ...DEFAULT_STATE };
  const itemsMap = new Map(snapshot.items.map((item) => [item.id, cloneItem(item)]));
  const target = itemsMap.get(itemId);
  if (!target) {
    return null;
  }
  const timestamp = Date.now();
  const mutation: Extract<InventoryCacheMutation, { kind: 'reorder' }> = {
    kind: 'reorder',
    itemId,
    expectedReorderPoint: reorderPoint,
    timestamp,
  };
  const pendingMutations = mergeReorderMutation(snapshot.pendingMutations, mutation);
  const updated = upsertItem(itemsMap, mutation);

  const nextState: InventoryCacheState = {
    items: Array.from(itemsMap.values()),
    pendingMutations,
    lastSyncedAt: snapshot.lastSyncedAt,
    lastNetworkVersion: snapshot.lastNetworkVersion,
  };
  persistState(tenantSlug, nextState);

  return updated ?? null;
};

export const mergeNetworkInventorySnapshot = (
  tenantSlug: string,
  networkItems: InventoryItemRecord[],
): { items: InventoryItemRecord[]; conflicts: InventoryConflictRecord[] } => {
  const snapshot = loadInventoryCache(tenantSlug) ?? { ...DEFAULT_STATE };
  const itemsMap = new Map(networkItems.map((item) => [item.id, cloneItem(item)]));
  const conflicts: InventoryConflictRecord[] = [];
  const retainedMutations: InventoryCacheMutation[] = [];
  const now = Date.now();

  for (const mutation of snapshot.pendingMutations) {
    const target = itemsMap.get(mutation.itemId);

    if (!target) {
      if (!mutation.lastConflictNotifiedAt) {
        conflicts.push({
          itemId: mutation.itemId,
          kind: mutation.kind,
          expected: mutation.kind === 'adjust' ? mutation.expectedOnHand : mutation.expectedReorderPoint,
          actual: null,
          delta: mutation.kind === 'adjust' ? mutation.delta : undefined,
          timestamp: mutation.timestamp,
        });
      }
      retainedMutations.push({
        ...mutation,
        lastConflictNotifiedAt: mutation.lastConflictNotifiedAt ?? now,
      });
      continue;
    }

    if (mutation.kind === 'adjust') {
      if (Math.abs(target.onHand - mutation.expectedOnHand) > 0.0001) {
        if (!mutation.lastConflictNotifiedAt) {
          conflicts.push({
            itemId: mutation.itemId,
            kind: 'adjust',
            expected: mutation.expectedOnHand,
            actual: target.onHand,
            delta: mutation.delta,
            timestamp: mutation.timestamp,
          });
        }
        const adjusted: InventoryItemRecord = {
          ...target,
          onHand: mutation.expectedOnHand,
          updatedAt: Math.max(target.updatedAt, mutation.timestamp),
        };
        itemsMap.set(adjusted.id, adjusted);
        retainedMutations.push({
          ...mutation,
          lastConflictNotifiedAt: mutation.lastConflictNotifiedAt ?? now,
        });
      } else if (target.updatedAt < mutation.timestamp) {
        itemsMap.set(target.id, {
          ...target,
          updatedAt: mutation.timestamp,
        });
      }
      continue;
    }

    if (mutation.kind === 'reorder') {
      if (Math.abs(target.reorderPoint - mutation.expectedReorderPoint) > 0.0001) {
        if (!mutation.lastConflictNotifiedAt) {
          conflicts.push({
            itemId: mutation.itemId,
            kind: 'reorder',
            expected: mutation.expectedReorderPoint,
            actual: target.reorderPoint,
            timestamp: mutation.timestamp,
          });
        }
        const updated: InventoryItemRecord = {
          ...target,
          reorderPoint: mutation.expectedReorderPoint,
          updatedAt: Math.max(target.updatedAt, mutation.timestamp),
        };
        itemsMap.set(updated.id, updated);
        retainedMutations.push({
          ...mutation,
          lastConflictNotifiedAt: mutation.lastConflictNotifiedAt ?? now,
        });
      } else if (target.updatedAt < mutation.timestamp) {
        itemsMap.set(target.id, {
          ...target,
          updatedAt: mutation.timestamp,
        });
      }
    }
  }

  const mergedItems = Array.from(itemsMap.values());
  const nextState: InventoryCacheState = {
    items: mergedItems,
    pendingMutations: retainedMutations,
    lastSyncedAt: now,
    lastNetworkVersion: computeInventoryVersion(networkItems),
  };
  persistState(tenantSlug, nextState);

  return { items: mergedItems, conflicts };
};
